import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { CityStatePanel } from './city/CityStatePanel';
import { clearCitySelection, loadCities, persistCitySelection, resolveCityContext, restoredCitySelection, type City, type CityContextStatus, type CityPolicy, type DetectedLocation } from './city/cityContext';
import { ProfileScreen, type SessionResponse } from './ProfileScreen';
import { VenueDetailScreen } from './VenueDetailScreen';
import { ActivationDetailScreen } from './screens/ActivationDetailScreen';
import { ExpiredScreen } from './screens/ExpiredScreen';
import { ImpressionEditScreen } from './screens/ImpressionEditScreen';
import { ImpressionFormScreen } from './screens/ImpressionFormScreen';
import { OffersScreen } from './screens/OffersScreen';
import { RedeemedScreen } from './screens/RedeemedScreen';
import { FeedScreen } from './social/FeedScreen';
import { FollowListScreen } from './social/FollowListScreen';
import { PulseSavedPlacesScreen, PulseVisitsScreen } from './social/PulseAuxiliaryScreens';
import { NotificationsScreen } from './social/NotificationsScreen';
import { UserProfileScreen } from './social/UserProfileScreen';
import {
  UserContextSheet,
  useUserContextActions,
  useUserContextData,
} from './userContext/UserContext';
import type { OfferContext } from './guestOffersApi';
import { fetchSavedVenueIds, saveVenue, unsaveVenue } from './mapSavedPlacesApi';
import { mapMarkerOptions } from './mapMarkers';
import {
  DesktopMapVenuePreview,
  MobileMapVenuePreview,
} from './map/MapReferenceVenuePreview';
import {
  AverageCheckFilter,
  normalizeAverageCheckTiers,
  type PriceTier,
  type VerifiedReceiptPrice,
} from './map/VerifiedAverageCheckBridge';
import { pushShellSectionPath, pushVenuePath, replaceWithMapPath, userContextDestinationForPath, venueIdForPath } from './shellHistory';
import { BottomSheet } from './ui/BottomSheet';
import { Icon } from './ui/Icons';
import { DesktopHeader, MobileBottomNav, MobileHeader, type ConsumerNavSection } from './ui/ConsumerNavigation';
import { HopperIcon } from './ui/icons/HopperIcon';
import { fetchVenueDetail, VenueDetailApiError, type VenueDetailData } from './venueDetailApi';
import { VENUE_CATEGORY_OPTIONS, VENUE_CATEGORY_ORDER, isVenueCategory, type VenueCategory } from './venueCategories';

type Coordinate = [number, number];
type NavSection = ConsumerNavSection;
type AppSection = NavSection
  | 'activation-detail'
  | 'redeemed'
  | 'expired'
  | 'impression-form'
  | 'impression-edit'
  | 'user-profile'
  | 'follow-list'
  | 'notifications'
  | 'my-visits'
  | 'my-saved'
  | 'my-friends';
type SocialReturnSection = 'feed' | 'follow-list' | 'notifications';
type FollowListMode = 'followers' | 'following';
type FollowListReturnSection = 'feed' | 'user-profile' | 'profile';

type AppProps = {
  dataRevision?: number;
  mapRevision?: number;
};

type Venue = {
  id: string;
  name: string | null;
  address: string | null;
  category: VenueCategory | null;
  opening_status: 'open' | 'closed' | 'unknown';
  verified_receipt_price: VerifiedReceiptPrice | null;
  location: {
    latitude: string;
    longitude: string;
  } | null;
  geocode_required: boolean;
};

type CityMapResponse = {
  city: City;
  venues: Venue[];
};

type FrontendConfigResponse = {
  frontend: {
    city_policy: CityPolicy;
    maps: {
      provider: string;
      configured: boolean;
      api_key: string | null;
    };
  };
};

type MapState = 'loading' | 'ready' | 'missing-key' | 'unavailable';
type MapFilters = {
  categories: VenueCategory[];
  avgCheck: PriceTier[];
  openNow: boolean;
  hasOffers: boolean;
  hasEvents: boolean;
};

type NearbyLocation = {
  latitude: number;
  longitude: number;
};

type CitySearchState = 'idle' | 'loading' | 'ready' | 'error';

const MAP_FILTER_KEYS = ['category', 'avg_check', 'open_now', 'has_offers', 'has_events'] as const;

function stableCategories(categories: VenueCategory[]): VenueCategory[] {
  const selected = new Set(categories);
  return VENUE_CATEGORY_ORDER.filter((category) => selected.has(category));
}

function initialMapFilters(): MapFilters {
  const params = new URLSearchParams(window.location.search);
  const categories = (params.get('category') ?? '')
    .split(',')
    .map((value) => value.trim())
    .filter(isVenueCategory);

  return {
    categories: stableCategories(categories),
    avgCheck: normalizeAverageCheckTiers(params.get('avg_check')),
    openNow: params.get('open_now') === '1',
    hasOffers: params.get('has_offers') === '1',
    hasEvents: params.get('has_events') === '1',
  };
}

function mapFilterQuery(filters: MapFilters): string {
  const parts: string[] = [];
  const categories = stableCategories(filters.categories);
  if (categories.length > 0) parts.push(`category=${categories.join(',')}`);
  if (filters.avgCheck.length > 0) parts.push(`avg_check=${filters.avgCheck.join(',')}`);
  if (filters.openNow) parts.push('open_now=1');
  if (filters.hasOffers) parts.push('has_offers=1');
  if (filters.hasEvents) parts.push('has_events=1');
  return parts.length > 0 ? `?${parts.join('&')}` : '';
}

function replaceMapFilterUrl(filters: MapFilters): void {
  const current = new URL(window.location.href);
  const preserved = new URLSearchParams(current.search);
  MAP_FILTER_KEYS.forEach((key) => preserved.delete(key));
  const preservedQuery = preserved.toString();
  const filterQuery = mapFilterQuery(filters).replace(/^\?/, '');
  const query = [preservedQuery, filterQuery].filter(Boolean).join('&');
  window.history.replaceState(window.history.state, '', `${current.pathname}${query ? `?${query}` : ''}${current.hash}`);
}

type YandexMapInstance = {
  geoObjects: {
    add(object: unknown): void;
    remove(object: unknown): void;
  };
  behaviors: {
    disable(name: string): void;
    enable(name: string): void;
  };
  container: {
    fitToViewport(): void;
  };
  setCenter(center: Coordinate, zoom?: number): void;
  destroy(): void;
};

type YandexPlacemark = {
  events?: {
    add(name: string, callback: () => void): void;
  };
  options?: {
    set(options: Record<string, unknown>): void;
  };
};

type HopperPlacemark = {
  venueId: string;
  placemark: YandexPlacemark;
};

type YandexMapsApi = {
  ready(callback: () => void): void;
  Map: new (
    element: HTMLElement,
    state: { center: Coordinate; zoom: number; controls?: string[] },
    options?: Record<string, unknown>,
  ) => YandexMapInstance;
  Placemark: new (
    coordinates: Coordinate,
    properties?: Record<string, unknown>,
    options?: Record<string, unknown>,
  ) => YandexPlacemark;
};

const YANDEX_SCRIPT_ID = 'hopper-yandex-maps-api';
const AUTH_RETURN_TARGET_KEY = 'hopper.authReturnTarget';

function authReturnTarget(): string | null {
  return sessionStorage.getItem(AUTH_RETURN_TARGET_KEY) ?? localStorage.getItem(AUTH_RETURN_TARGET_KEY);
}

function initialAppSection(): AppSection {
  const userContextDestination = userContextDestinationForPath();
  if (userContextDestination === 'visits') return 'my-visits';
  if (userContextDestination === 'saved') return 'my-saved';
  if (userContextDestination === 'friends') return 'my-friends';

  const url = new URL(window.location.href);
  const isAuthReturn = url.searchParams.has('auth') || url.searchParams.has('auth_error');

  if (isAuthReturn && authReturnTarget() === 'profile') {
    return 'profile';
  }

  return 'map';
}

function activeNavSection(section: AppSection): NavSection {
  if (section === 'activation-detail' || section === 'redeemed' || section === 'expired') {
    return 'offers';
  }

  if (section === 'impression-form' || section === 'impression-edit') {
    return 'profile';
  }

  if (section === 'user-profile' || section === 'follow-list' || section === 'notifications') {
    return 'feed';
  }

  if (section === 'my-visits' || section === 'my-saved' || section === 'my-friends') {
    return 'profile';
  }

  return section;
}

function toCoordinate(latitude: string | null, longitude: string | null): Coordinate | null {
  if (latitude === null || longitude === null) {
    return null;
  }

  const lat = Number(latitude);
  const lng = Number(longitude);

  return Number.isFinite(lat) && Number.isFinite(lng) ? [lat, lng] : null;
}

function getYandexMaps(): YandexMapsApi | undefined {
  return (window as Window & { ymaps?: YandexMapsApi }).ymaps;
}

function loadYandexMaps(apiKey: string): Promise<YandexMapsApi> {
  const existing = getYandexMaps();

  if (existing) {
    return new Promise((resolve) => existing.ready(() => resolve(existing)));
  }

  return new Promise((resolve, reject) => {
    let script = document.getElementById(YANDEX_SCRIPT_ID) as HTMLScriptElement | null;

    const handleReady = () => {
      const ymaps = getYandexMaps();

      if (!ymaps) {
        reject(new Error('Yandex Maps API did not initialize'));
        return;
      }

      ymaps.ready(() => resolve(ymaps));
    };

    if (script) {
      script.addEventListener('load', handleReady, { once: true });
      script.addEventListener('error', () => reject(new Error('Yandex Maps API failed to load')), {
        once: true,
      });
      return;
    }

    script = document.createElement('script');
    script.id = YANDEX_SCRIPT_ID;
    script.async = true;
    script.src = `https://api-maps.yandex.ru/2.1/?apikey=${encodeURIComponent(apiKey)}&lang=ru_RU`;
    script.addEventListener('load', handleReady, { once: true });
    script.addEventListener('error', () => reject(new Error('Yandex Maps API failed to load')), {
      once: true,
    });
    document.head.append(script);
  });
}

function venueCoordinate(venue: Venue): Coordinate | null {
  if (!venue.location) {
    return null;
  }

  return toCoordinate(venue.location.latitude, venue.location.longitude);
}

function YandexMap({
  city,
  venues,
  apiKey,
  selectedVenueId,
  focusVenueId,
  onVenueSelect,
  discoveryStatus,
  visible,
}: {
  city: City;
  venues: Venue[];
  apiKey: string | null;
  selectedVenueId: string | null;
  focusVenueId: string | null;
  onVenueSelect: (venueId: string) => void;
  discoveryStatus: string | null;
  visible: boolean;
}) {
  const containerRef = useRef<HTMLDivElement>(null);
  const mapRef = useRef<YandexMapInstance | null>(null);
  const ymapsRef = useRef<YandexMapsApi | null>(null);
  const hopperPlacemarksRef = useRef<HopperPlacemark[]>([]);
  const onVenueSelectRef = useRef(onVenueSelect);
  const cityRef = useRef(city);
  const centeredCityIdRef = useRef<string | null>(null);
  const [state, setState] = useState<MapState>(apiKey ? 'loading' : 'missing-key');
  const [mapRevision, setMapRevision] = useState(0);

  onVenueSelectRef.current = onVenueSelect;
  cityRef.current = city;

  useEffect(() => {
    if (!apiKey) {
      setState('missing-key');
      return;
    }

    const initialCity = cityRef.current;
    const center = toCoordinate(initialCity.center.latitude, initialCity.center.longitude);
    if (!center || !containerRef.current) {
      setState('unavailable');
      return;
    }

    let cancelled = false;
    let ownedMap: YandexMapInstance | null = null;
    setState('loading');

    loadYandexMaps(apiKey)
      .then((ymaps) => {
        if (cancelled || !containerRef.current || mapRef.current) {
          return;
        }

        const map = new ymaps.Map(containerRef.current, {
          center,
          zoom: 14,
          controls: ['zoomControl'],
        });
        ownedMap = map;
        mapRef.current = map;
        ymapsRef.current = ymaps;
        centeredCityIdRef.current = initialCity.id;
        map.behaviors.enable('scrollZoom');
        setState('ready');
        setMapRevision((value) => value + 1);
      })
      .catch(() => {
        if (!cancelled) {
          setState('unavailable');
        }
      });

    return () => {
      cancelled = true;

      if (ownedMap && mapRef.current === ownedMap) {
        hopperPlacemarksRef.current = [];
        ymapsRef.current = null;
        mapRef.current = null;
        centeredCityIdRef.current = null;
        ownedMap.destroy();
      }
    };
  }, [apiKey]);

  useEffect(() => {
    const map = mapRef.current;
    const ymaps = ymapsRef.current;
    if (!map || !ymaps) {
      return;
    }

    for (const { placemark } of hopperPlacemarksRef.current) {
      map.geoObjects.remove(placemark);
    }

    const nextPlacemarks: HopperPlacemark[] = [];

    for (const venue of venues) {
      const coordinates = venueCoordinate(venue);
      if (!coordinates) {
        continue;
      }

      const placemark = new ymaps.Placemark(
        coordinates,
        {
          hintContent: venue.name ?? 'Место Хоппер',
        },
        mapMarkerOptions(venue.id === selectedVenueId ? 'selected' : 'default'),
      );

      placemark.events?.add('click', () => {
        window.requestAnimationFrame(() => onVenueSelectRef.current(venue.id));
      });
      map.geoObjects.add(placemark);
      nextPlacemarks.push({ venueId: venue.id, placemark });
    }

    hopperPlacemarksRef.current = nextPlacemarks;
  }, [mapRevision, venues]);

  useEffect(() => {
    for (const { venueId, placemark } of hopperPlacemarksRef.current) {
      placemark.options?.set(mapMarkerOptions(venueId === selectedVenueId ? 'selected' : 'default'));
    }
  }, [mapRevision, selectedVenueId]);

  useEffect(() => {
    const map = mapRef.current;
    if (!map || centeredCityIdRef.current === city.id) {
      return;
    }

    const center = toCoordinate(city.center.latitude, city.center.longitude);
    if (!center) {
      return;
    }

    map.setCenter(center, 14);
    centeredCityIdRef.current = city.id;
  }, [city.center.latitude, city.center.longitude, city.id, mapRevision]);

  useEffect(() => {
    if (!visible || !focusVenueId) return;
    const map = mapRef.current;
    const venue = venues.find((item) => item.id === focusVenueId);
    const coordinate = venue ? venueCoordinate(venue) : null;
    if (map && coordinate) map.setCenter(coordinate, 15);
  }, [focusVenueId, mapRevision, venues, visible]);

  useEffect(() => {
    if (!visible) {
      return;
    }

    const map = mapRef.current;
    if (!map) {
      return;
    }

    const frame = window.requestAnimationFrame(() => {
      if (mapRef.current === map) {
        map.container.fitToViewport();
      }
    });

    return () => window.cancelAnimationFrame(frame);
  }, [mapRevision, visible]);

  return (
    <div className="map-stage">
      <div className="map-canvas" ref={containerRef} aria-label={`Карта города ${city.name}`} />

      {state !== 'ready' && (
        <div className="map-state-card" role="status">
          {state === 'loading' && (
            <>
              <span className="map-state-card__pulse" />
              <strong>Загружаем карту {city.name}</strong>
              <span>Подключаем городской слой и точки Хоппер.</span>
            </>
          )}

          {state === 'missing-key' && (
            <>
              <strong>Карта пока недоступна</strong>
              <span>Не удалось открыть картографический слой. Попробуйте позже.</span>
            </>
          )}

          {state === 'unavailable' && (
            <>
              <strong>Карта временно недоступна</strong>
              <span>Места уже загружены, но сама карта не ответила. Попробуйте обновить страницу.</span>
            </>
          )}
        </div>
      )}

      {discoveryStatus && <div className="map-discovery-status" role="status">{discoveryStatus}</div>}
    </div>
  );
}

function toggleMapCategory(filters: MapFilters, category: VenueCategory): MapFilters {
  const selected = new Set(filters.categories);
  if (selected.has(category)) selected.delete(category);
  else selected.add(category);
  return { ...filters, categories: stableCategories([...selected]) };
}

function CategoryChips({
  filters,
  onChange,
}: {
  filters: MapFilters;
  onChange: (filters: MapFilters) => void;
}) {
  return (
    <div className="chip-grid map-category-chips">
      <button
        className={filters.categories.length === 0 ? 'chip is-selected' : 'chip'}
        type="button"
        onClick={() => onChange({ ...filters, categories: [] })}
      >
        Все
      </button>
      {VENUE_CATEGORY_OPTIONS.map((option) => (
        <button
          className={filters.categories.includes(option.value) ? 'chip is-selected' : 'chip'}
          key={option.value}
          type="button"
          aria-pressed={filters.categories.includes(option.value)}
          onClick={() => onChange(toggleMapCategory(filters, option.value))}
        >
          {option.label}
        </button>
      ))}
    </div>
  );
}

function DiscoveryToggles({ filters, onChange }: { filters: MapFilters; onChange: (filters: MapFilters) => void }) {
  const toggles = [
    { key: 'openNow' as const, label: 'Открыто сейчас', icon: 'clock' as const },
    { key: 'hasOffers' as const, label: 'Есть предложения', icon: 'gift' as const },
    { key: 'hasEvents' as const, label: 'Есть события', icon: 'calendar' as const },
  ];

  return (
    <div className="map-filter-toggles">
      {toggles.map((toggle) => (
        <button
          className={filters[toggle.key] ? 'map-filter-toggle is-active' : 'map-filter-toggle'}
          key={toggle.key}
          type="button"
          role="switch"
          aria-checked={filters[toggle.key]}
          onClick={() => onChange({ ...filters, [toggle.key]: !filters[toggle.key] })}
        >
          <span className="map-filter-toggle__label">
            <HopperIcon name={toggle.icon} size={17} />
            {toggle.label}
          </span>
          <span className="map-filter-toggle__switch" aria-hidden="true"><span /></span>
        </button>
      ))}
    </div>
  );
}

function NearbyToggle({
  active,
  requesting,
  error,
  onToggle,
}: {
  active: boolean;
  requesting: boolean;
  error: string | null;
  onToggle: () => void;
}) {
  return (
    <div className="map-nearby-filter">
      <button
        className={active ? 'map-filter-toggle is-active' : 'map-filter-toggle'}
        type="button"
        role="switch"
        aria-checked={active}
        disabled={requesting}
        onClick={onToggle}
      >
        <span className="map-filter-toggle__label">
          <HopperIcon name="location" size={17} />
          {requesting ? 'Определяем местоположение…' : 'Рядом'}
        </span>
        <span className="map-filter-toggle__switch" aria-hidden="true"><span /></span>
      </button>
      {error && <div className="map-filter-feedback" role="status">{error}</div>}
    </div>
  );
}

function FilterRail({
  filters,
  onChange,
  onReset,
  nearbyActive,
  nearbyRequesting,
  nearbyError,
  onToggleNearby,
  refreshing,
  error,
  resultCount,
}: {
  filters: MapFilters;
  onChange: (filters: MapFilters) => void;
  onReset: () => void;
  nearbyActive: boolean;
  nearbyRequesting: boolean;
  nearbyError: string | null;
  onToggleNearby: () => void;
  refreshing: boolean;
  error: string | null;
  resultCount: number | null;
}) {
  const hasFilters = filters.categories.length > 0
    || filters.avgCheck.length > 0
    || filters.openNow
    || filters.hasOffers
    || filters.hasEvents
    || nearbyActive;

  return (
    <aside className="filter-rail" aria-label="Фильтры карты">
      <section className="panel-card filters-card">
        <div className="panel-heading">
          <h2>Фильтры</h2>
          <button className="text-action" type="button" disabled={!hasFilters} onClick={onReset}>
            Сбросить все
          </button>
        </div>

        <div className="filter-group">
          <strong>Категории</strong>
          <CategoryChips filters={filters} onChange={onChange} />
        </div>

        <div className="filter-group">
          <strong>Средний чек</strong>
          <AverageCheckFilter
            tiers={filters.avgCheck}
            onChange={(avgCheck) => onChange({ ...filters, avgCheck })}
          />
        </div>

        <div className="filter-group">
          <strong>Сейчас важно</strong>
          <div className="map-filter-stack">
            <DiscoveryToggles filters={filters} onChange={onChange} />
            <NearbyToggle
              active={nearbyActive}
              requesting={nearbyRequesting}
              error={nearbyError}
              onToggle={onToggleNearby}
            />
          </div>
        </div>

        <div className="map-filter-feedback" aria-live="polite">
          {refreshing ? 'Обновляем места…' : error ?? (resultCount !== null ? `Найдено мест: ${resultCount}` : 'Выберите условия для поиска.')}
        </div>
      </section>
    </aside>
  );
}

function VenuePanel({
  venue,
  detail,
  loading,
  authenticated,
  saved,
  saving,
  onToggleSaved,
  onOpen,
  onOpenOffers,
}: {
  venue: Venue | null;
  detail: VenueDetailData | null;
  loading: boolean;
  authenticated: boolean;
  saved: boolean;
  saving: boolean;
  onToggleSaved: () => void;
  onOpen: () => void;
  onOpenOffers: () => void;
}) {
  if (!venue) {
    return (
      <aside className="venue-panel panel-card venue-panel--empty">
        <Icon name="map" />
        <strong>Выберите место на карте</strong>
        <span>Здесь появится карточка выбранного заведения.</span>
      </aside>
    );
  }

  return (
    <aside className="venue-panel panel-card map-venue-panel">
      <DesktopMapVenuePreview
        venue={venue}
        detail={detail}
        loading={loading}
        authenticated={authenticated}
        saved={saved}
        saving={saving}
        onToggleSaved={onToggleSaved}
        onOpen={onOpen}
        onOpenOffers={onOpenOffers}
      />
    </aside>
  );
}

function MobileVenueSheet({
  venue,
  detail,
  expanded,
  authenticated,
  saved,
  saving,
  onToggleSaved,
  onToggle,
  onClose,
  onOpen,
  onOpenOffers,
}: {
  venue: Venue | null;
  detail: VenueDetailData | null;
  authenticated: boolean;
  saved: boolean;
  saving: boolean;
  onToggleSaved: () => void;
  onOpen: () => void;
  onOpenOffers: () => void;
  onToggle: () => void;
  onClose: () => void;
  expanded: boolean;
}) {
  if (!venue) {
    return null;
  }

  return (
    <BottomSheet
      open
      persistent
      expanded={expanded}
      onToggle={onToggle}
      onClose={onClose}
      showClose={false}
      rootClassName="map-mobile-bottom-sheet"
      className="map-mobile-bottom-sheet__panel"
      maxHeight={expanded ? '56dvh' : '226px'}
    >
      <MobileMapVenuePreview
        venue={venue}
        detail={detail}
        loading={false}
        authenticated={authenticated}
        saved={saved}
        saving={saving}
        onToggleSaved={onToggleSaved}
        onOpen={onOpen}
        onOpenOffers={onOpenOffers}
        expanded={expanded}
        onClose={onClose}
      />
    </BottomSheet>
  );
}

export function App({ dataRevision = 0, mapRevision = 0 }: AppProps) {
  const initialVenueId = venueIdForPath();
  const [activeSection, setActiveSection] = useState<AppSection>(initialAppSection);
  const [mapData, setMapData] = useState<CityMapResponse | null>(null);
  const [mapsKey, setMapsKey] = useState<string | null>(null);
  const [mapFilters, setMapFilters] = useState<MapFilters>(initialMapFilters);
  const [mapFiltersOpen, setMapFiltersOpen] = useState(false);
  const [mapRefreshing, setMapRefreshing] = useState(false);
  const [mapFilterError, setMapFilterError] = useState<string | null>(null);
  const [mapLoadRevision, setMapLoadRevision] = useState(0);
  const [nearbyLocation, setNearbyLocation] = useState<NearbyLocation | null>(null);
  const [nearbyRequesting, setNearbyRequesting] = useState(false);
  const [nearbyPermissionError, setNearbyPermissionError] = useState<string | null>(null);
  const [session, setSession] = useState<SessionResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedVenueId, setSelectedVenueId] = useState<string | null>(initialVenueId);
  const [pulseSelectedVenue, setPulseSelectedVenue] = useState<Venue | null>(null);
  const [pulseFocusVenueId, setPulseFocusVenueId] = useState<string | null>(null);
  const [venueDetail, setVenueDetail] = useState<VenueDetailData | null>(null);
  const [venueDetailLoading, setVenueDetailLoading] = useState(Boolean(initialVenueId));
  const [venueDetailRevision, setVenueDetailRevision] = useState(0);
  const [mobileDetailExpanded, setMobileDetailExpanded] = useState(false);
  const [venueScreenOpen, setVenueScreenOpen] = useState(Boolean(initialVenueId));
  const [savedVenueIds, setSavedVenueIds] = useState<Set<string>>(() => new Set());
  const [savingVenueId, setSavingVenueId] = useState<string | null>(null);
  const [offersMode, setOffersMode] = useState<'city' | 'venue' | null>(null);
  const [currentOfferId, setCurrentOfferId] = useState<string | null>(null);
  const [currentActivationId, setCurrentActivationId] = useState<string | null>(null);
  const [currentVisitId, setCurrentVisitId] = useState<string | null>(null);
  const [currentImpressionId, setCurrentImpressionId] = useState<string | null>(null);
  const [currentImpressionVenueName, setCurrentImpressionVenueName] = useState<string>('месте Хоппера');
  const [impressionReturnVenueId, setImpressionReturnVenueId] = useState<string | null>(null);
  const [profileFlash, setProfileFlash] = useState<string | null>(null);
  const [currentSocialUserId, setCurrentSocialUserId] = useState<string | null>(null);
  const [socialReturnSection, setSocialReturnSection] = useState<SocialReturnSection>('feed');
  const [followListUserId, setFollowListUserId] = useState<string | null>(null);
  const [followListMode, setFollowListMode] = useState<FollowListMode>('following');
  const [followListReturnSection, setFollowListReturnSection] = useState<FollowListReturnSection>('feed');
  const [userContextOpen, setUserContextOpen] = useState(false);
  const [activeCity, setActiveCity] = useState<City | null>(null);
  const [cityPolicy, setCityPolicy] = useState<CityPolicy | null>(null);
  const [cityStatus, setCityStatus] = useState<CityContextStatus>('location_unresolved');
  const [cityResolving, setCityResolving] = useState(true);
  const [detectedLocation, setDetectedLocation] = useState<DetectedLocation | null>(null);
  const [citySelectorOpen, setCitySelectorOpen] = useState(false);
  const [cityChoices, setCityChoices] = useState<City[]>([]);
  const [citySearch, setCitySearch] = useState('');
  const [citySearchState, setCitySearchState] = useState<CitySearchState>('idle');
  const [citySearchRevision, setCitySearchRevision] = useState(0);
  const [citySelectionError, setCitySelectionError] = useState<string | null>(null);
  const [choosingCityId, setChoosingCityId] = useState<string | null>(null);
  const cityChoiceRevision = useRef(0);

  // Profile uses document scrolling; opening an impression must not preserve its
  // visit-list scroll offset beneath the sticky shared header. Reset before paint.
  useLayoutEffect(() => {
    if (activeSection === 'impression-form' || activeSection === 'impression-edit') {
      window.scrollTo(0, 0);
    }
  }, [activeSection]);

  useEffect(() => {
    const mirrorAuthReturnTarget = () => {
      if (sessionStorage.getItem(AUTH_RETURN_TARGET_KEY) === 'profile') {
        localStorage.setItem(AUTH_RETURN_TARGET_KEY, 'profile');
      }
    };

    window.addEventListener('pagehide', mirrorAuthReturnTarget);

    return () => window.removeEventListener('pagehide', mirrorAuthReturnTarget);
  }, []);

  useEffect(() => {
    sessionStorage.removeItem(AUTH_RETURN_TARGET_KEY);
    localStorage.removeItem(AUTH_RETURN_TARGET_KEY);
  }, []);

  useEffect(() => {
    const handleHistoryVenue = (event: Event) => {
      const venueId = (event as CustomEvent<{ venueId?: string }>).detail?.venueId;
      if (!venueId) return;

      setSelectedVenueId(venueId);
      setMobileDetailExpanded(false);
      setActiveSection('map');
      setVenueScreenOpen(true);
    };

    window.addEventListener('hopper:history-venue', handleHistoryVenue);
    return () => window.removeEventListener('hopper:history-venue', handleHistoryVenue);
  }, []);

  useEffect(() => {
    const handleUserContextHistory = (event: Event) => {
      const destination = (event as CustomEvent<{ destination?: string }>).detail?.destination;
      if (destination === 'visits') setActiveSection('my-visits');
      else if (destination === 'saved') setActiveSection('my-saved');
      else if (destination === 'friends') setActiveSection('my-friends');
      else return;
      setVenueScreenOpen(false);
      setUserContextOpen(false);
    };

    window.addEventListener('hopper:history-user-context', handleUserContextHistory);
    return () => window.removeEventListener('hopper:history-user-context', handleUserContextHistory);
  }, []);

  useEffect(() => {
    const handleOpenImpression = (event: Event) => {
      const detail = (event as CustomEvent<{ visitId?: string; venueName?: string }>).detail;
      if (!detail?.visitId) {
        return;
      }

      setCurrentVisitId(detail.visitId);
      setCurrentImpressionId(null);
      setCurrentImpressionVenueName(detail.venueName?.trim() || 'месте Хоппера');
      setImpressionReturnVenueId(null);
      setProfileFlash(null);
      setVenueScreenOpen(false);
      setActiveSection('impression-form');
    };

    window.addEventListener('hopper:open-impression', handleOpenImpression);
    return () => window.removeEventListener('hopper:open-impression', handleOpenImpression);
  }, []);

  const mapQuery = useMemo(() => mapFilterQuery(mapFilters), [mapFilters]);
  const mapRequestQuery = useMemo(() => {
    const params = new URLSearchParams(mapQuery.replace(/^\?/, ''));
    if (nearbyLocation) {
      params.set('nearby', '1');
      params.set('lat', String(nearbyLocation.latitude));
      params.set('lng', String(nearbyLocation.longitude));
    }
    const query = params.toString();
    return query ? `?${query}` : '';
  }, [mapQuery, nearbyLocation]);

  const handleToggleNearby = useCallback(() => {
    if (nearbyLocation) {
      setNearbyLocation(null);
      setNearbyPermissionError(null);
      return;
    }

    if (!navigator.geolocation) {
      setNearbyPermissionError('Требуется разрешение на геолокацию');
      return;
    }

    setNearbyRequesting(true);
    setNearbyPermissionError(null);
    navigator.geolocation.getCurrentPosition(
      (position) => {
        setNearbyLocation({ latitude: position.coords.latitude, longitude: position.coords.longitude });
        setNearbyRequesting(false);
      },
      () => {
        setNearbyLocation(null);
        setNearbyPermissionError('Требуется разрешение на геолокацию');
        setNearbyRequesting(false);
      },
      { enableHighAccuracy: false, timeout: 12000, maximumAge: 0 },
    );
  }, [nearbyLocation]);

  useEffect(() => {
    replaceMapFilterUrl(mapFilters);
  }, [mapFilters]);

  useEffect(() => {
    const controller = new AbortController();
    fetch('/api/v1/frontend-config', { signal: controller.signal })
      .then((response) => {
        if (!response.ok) throw new Error(`Frontend config failed: ${response.status}`);
        return response.json() as Promise<FrontendConfigResponse>;
      })
      .then((config) => { setMapsKey(config.frontend.maps.api_key); setCityPolicy(config.frontend.city_policy); })
      .catch((reason: unknown) => {
        if (reason instanceof DOMException && reason.name === 'AbortError') return;
        setMapsKey(null);
        setCityResolving(false);
      });

    return () => controller.abort();
  }, []);

  const applyCityResolution = useCallback((result: Awaited<ReturnType<typeof resolveCityContext>>, source: 'manual' | 'geolocation' | 'restored') => {
    setCityPolicy(result.policy);
    setCityStatus(result.status);
    setCityResolving(false);
    setDetectedLocation(result.detected_location);
    setActiveCity(result.status === 'available' ? result.city : null);
    if (result.status === 'available' && result.city && source !== 'restored') persistCitySelection(result.city, source === 'manual');
    if (source === 'restored' && result.status !== 'available') clearCitySelection();
  }, []);

  const locateCity = useCallback(() => {
    const revision = ++cityChoiceRevision.current;
    setCityResolving(true);
    if (!navigator.geolocation) { setCityStatus('location_unresolved'); setCityResolving(false); return; }
    navigator.geolocation.getCurrentPosition(
      (position) => {
        void resolveCityContext({ latitude: position.coords.latitude, longitude: position.coords.longitude })
          .then((result) => { if (revision === cityChoiceRevision.current) applyCityResolution(result, 'geolocation'); })
          .catch(() => { if (revision === cityChoiceRevision.current) { setCityStatus('location_unresolved'); setCityResolving(false); } });
      },
      () => { if (revision === cityChoiceRevision.current) { setCityStatus('location_unresolved'); setCityResolving(false); } },
      { enableHighAccuracy: false, timeout: 12000, maximumAge: 300000 },
    );
  }, [applyCityResolution]);

  const chooseCity = useCallback((city: City) => {
    if (choosingCityId) return;

    const revision = ++cityChoiceRevision.current;
    setChoosingCityId(city.id);
    setCitySelectionError(null);

    void resolveCityContext({ city: city.id })
      .then((result) => {
        if (revision !== cityChoiceRevision.current) return;
        applyCityResolution(result, 'manual');
        if (result.status === 'available') {
          setCitySelectorOpen(false);
          setMapData(null);
          setSelectedVenueId(null);
          setVenueScreenOpen(false);
        } else {
          setCitySelectionError('Этот город сейчас недоступен. Выберите другой город.');
        }
      })
      .catch(() => {
        if (revision === cityChoiceRevision.current) {
          setCityStatus('location_unresolved');
          setCitySelectionError('Не удалось проверить город. Попробуйте ещё раз.');
        }
      })
      .finally(() => {
        if (revision === cityChoiceRevision.current) setChoosingCityId(null);
      });
  }, [applyCityResolution, choosingCityId]);

  useEffect(() => {
    if (!cityPolicy) return;
    const revision = ++cityChoiceRevision.current;
    const restored = restoredCitySelection();
    if (restored) {
      setCityResolving(true);
      void resolveCityContext({ city: restored.city }).then((result) => {
        if (revision === cityChoiceRevision.current) applyCityResolution(result, 'restored');
      }).catch(() => {
        if (revision === cityChoiceRevision.current) {
          clearCitySelection();
          setCityResolving(false);
        }
      });
      return;
    }

    const permissions = navigator.permissions;
    if (!permissions?.query || !navigator.geolocation) { setCityResolving(false); return; }
    void permissions.query({ name: 'geolocation' }).then((state) => {
      if (revision !== cityChoiceRevision.current) return;
      if (state.state === 'granted') locateCity();
      else setCityResolving(false);
    }).catch(() => {
      if (revision === cityChoiceRevision.current) setCityResolving(false);
    });
  }, [cityPolicy?.revision, applyCityResolution, locateCity]);

  useEffect(() => {
    if (!citySelectorOpen || cityPolicy?.mode === 'pilot_only') return undefined;

    const controller = new AbortController();
    const timer = window.setTimeout(() => {
      setCitySearchState('loading');
      setCitySelectionError(null);

      void loadCities(citySearch, controller.signal)
        .then((cities) => {
          setCityChoices(cities);
          setCitySearchState('ready');
        })
        .catch((reason: unknown) => {
          if (reason instanceof DOMException && reason.name === 'AbortError') return;
          setCityChoices([]);
          setCitySearchState('error');
        });
    }, 180);

    return () => {
      window.clearTimeout(timer);
      controller.abort();
    };
  }, [citySelectorOpen, citySearch, citySearchRevision, cityPolicy?.mode, cityPolicy?.revision]);

  useEffect(() => {
    const onVisible = () => {
      if (document.visibilityState !== 'visible') return;
      fetch('/api/v1/frontend-config', { headers: { Accept: 'application/json' } })
        .then((response) => response.ok ? response.json() as Promise<FrontendConfigResponse> : Promise.reject())
        .then((config) => setCityPolicy(config.frontend.city_policy))
        .catch(() => undefined);
    };
    document.addEventListener('visibilitychange', onVisible);
    return () => document.removeEventListener('visibilitychange', onVisible);
  }, []);

  useEffect(() => {
    if (!activeCity) { setMapData(null); setLoading(false); setMapRefreshing(false); return; }
    const controller = new AbortController();
    const hasPreviousMap = mapData !== null;
    if (hasPreviousMap) setMapRefreshing(true);
    else setLoading(true);
    setMapFilterError(null);
    setError(null);

    fetch(`/api/v1/cities/${encodeURIComponent(activeCity.slug)}/map${mapRequestQuery}`, { signal: controller.signal })
      .then(async (response) => {
        if (response.status === 403) {
          const body = await response.json() as { error?: { code?: string; policy?: CityPolicy } };
          if (body.error?.code === 'city_pilot_restricted') {
            if (body.error.policy) setCityPolicy(body.error.policy);
            setDetectedLocation({ name: activeCity.name, region_name: activeCity.region_name, country_code: activeCity.country_code });
            setCityStatus('pilot_restricted');
            setActiveCity(null);
            setMapData(null);
            throw new Error('city_pilot_restricted');
          }
        }
        if (!response.ok) throw new Error(`City map failed: ${response.status}`);
        return response.json() as Promise<CityMapResponse>;
      })
      .then((cityMap) => {
        setMapData(cityMap);
        setSelectedVenueId((current) => {
          if (current && venueIdForPath() === current) return current;
          return current && cityMap.venues.some((venue) => venue.id === current) ? current : null;
        });
        setLoading(false);
        setMapRefreshing(false);
      })
      .catch((reason: unknown) => {
        if (reason instanceof DOMException && reason.name === 'AbortError') return;
        if (reason instanceof Error && reason.message === 'city_pilot_restricted') { setLoading(false); setMapRefreshing(false); return; }
        if (hasPreviousMap) setMapFilterError('Не удалось обновить результаты. Показываем предыдущие места.');
        else setError('Не удалось загрузить городской слой Хоппер.');
        setLoading(false);
        setMapRefreshing(false);
      });

    return () => controller.abort();
  }, [activeCity?.id, dataRevision, mapLoadRevision, mapRevision, mapRequestQuery]);

  useEffect(() => {
    const controller = new AbortController();

    fetch('/api/v1/auth/session', {
      signal: controller.signal,
      credentials: 'same-origin',
      headers: { Accept: 'application/json' },
    })
      .then((response) => {
        if (!response.ok) {
          throw new Error(`Auth session failed: ${response.status}`);
        }
        return response.json() as Promise<SessionResponse>;
      })
      .then((nextSession) => setSession(nextSession))
      .catch((reason: unknown) => {
        if (reason instanceof DOMException && reason.name === 'AbortError') {
          return;
        }
        setSession(null);
      });

    return () => controller.abort();
  }, []);

  useEffect(() => {
    if (session?.authenticated !== true) {
      setSavedVenueIds(new Set());
      return undefined;
    }

    let cancelled = false;

    void fetchSavedVenueIds()
      .then((ids) => {
        if (!cancelled) setSavedVenueIds(ids);
      })
      .catch(() => {
        if (!cancelled) setSavedVenueIds(new Set());
      });

    return () => {
      cancelled = true;
    };
  }, [session?.authenticated]);

  useEffect(() => {
    if (!selectedVenueId) {
      setVenueDetail(null);
      setVenueDetailLoading(false);
      return;
    }

    const controller = new AbortController();
    setVenueDetail(null);
    setVenueDetailLoading(true);

    void fetchVenueDetail(selectedVenueId, controller.signal)
      .then((response) => {
        setVenueDetail(response.venue);
        setVenueDetailLoading(false);
      })
      .catch((reason: unknown) => {
        if (reason instanceof DOMException && reason.name === 'AbortError') {
          return;
        }
        if (reason instanceof VenueDetailApiError && reason.status === 403 && reason.code === 'city_pilot_restricted') {
          setCityStatus('pilot_restricted');
          setActiveCity(null);
          setVenueScreenOpen(false);
        }
        setVenueDetail(null);
        setVenueDetailLoading(false);
      });

    return () => controller.abort();
  }, [dataRevision, selectedVenueId, venueDetailRevision]);

  const venues = useMemo(() => {
    const base = mapData?.venues ?? [];
    if (!pulseSelectedVenue || !activeCity || !mapData
        || mapData.city.id !== activeCity.id || pulseSelectedVenue.id !== selectedVenueId
        || pulseFocusVenueId !== selectedVenueId) return base;
    return base.some((venue) => venue.id === pulseSelectedVenue.id)
      ? base
      : [...base, pulseSelectedVenue];
  }, [activeCity, mapData, pulseFocusVenueId, pulseSelectedVenue, selectedVenueId]);
  const selectedVenue = useMemo(
    () => venues.find((venue) => venue.id === selectedVenueId) ?? null,
    [selectedVenueId, venues],
  );

  const offerContext = useMemo<OfferContext>(() => ({
    mode: offersMode ?? 'city',
    city: activeCity?.slug ?? mapData?.city.slug ?? '',
    venueId: offersMode === 'venue' ? selectedVenueId ?? undefined : undefined,
  }), [activeCity?.slug, mapData?.city.slug, offersMode, selectedVenueId]);

  const handleVenueSelect = useCallback((venueId: string) => {
    setPulseFocusVenueId(null);
    setSelectedVenueId(venueId);
    setMobileDetailExpanded(false);
    setVenueScreenOpen(false);
  }, []);

  const clearMapVenueSelection = useCallback(() => {
    setSelectedVenueId(null);
    setVenueDetail(null);
    setVenueDetailLoading(false);
    setMobileDetailExpanded(false);
    setVenueScreenOpen(false);
  }, []);

  const openVenueDetail = useCallback((venueId: string, pushHistory = true) => {
    setSelectedVenueId(venueId);
    setMobileDetailExpanded(false);
    setActiveSection('map');
    setVenueScreenOpen(true);
    if (pushHistory) pushVenuePath(venueId);
  }, []);

  const closeVenueDetail = useCallback(() => {
    setVenueScreenOpen(false);
    replaceWithMapPath();
  }, []);

  const handleToggleSaved = useCallback(async (venueId: string) => {
    if (session?.authenticated !== true || savingVenueId !== null) {
      return;
    }

    const wasSaved = savedVenueIds.has(venueId);
    setSavingVenueId(venueId);

    try {
      if (wasSaved) {
        await unsaveVenue(venueId);
      } else {
        await saveVenue(venueId);
      }

      setSavedVenueIds((current) => {
        const next = new Set(current);
        if (wasSaved) next.delete(venueId);
        else next.add(venueId);
        return next;
      });
    } catch {
      // Keep the last server-confirmed saved state on request failure.
    } finally {
      setSavingVenueId(null);
    }
  }, [savedVenueIds, savingVenueId, session?.authenticated]);

  const handleNavigate = useCallback((section: NavSection) => {
    setActiveSection(section);
    setVenueScreenOpen(false);

    if (section !== 'profile') {
      setProfileFlash(null);
    }

    if (section === 'offers') {
      setOffersMode('city');
      setCurrentOfferId(null);
      setCurrentActivationId(null);
    }
  }, []);

  const handleOpenSelectedVenueOffers = useCallback(() => {
    if (!selectedVenueId) return;
    setOffersMode('venue');
    setCurrentOfferId(null);
    setCurrentActivationId(null);
    setVenueScreenOpen(false);
    setActiveSection('offers');
  }, [selectedVenueId]);

  const handleActivationOpen = useCallback((activationId: string, offerId: string) => {
    setCurrentActivationId(activationId);
    setCurrentOfferId(offerId);
    setActiveSection('activation-detail');
  }, []);

  const handleBackToOffers = useCallback(() => {
    setCurrentActivationId(null);
    setCurrentOfferId(null);
    setActiveSection('offers');
  }, []);

  const handleCreateImpression = useCallback((visitId: string, venueName: string) => {
    setCurrentVisitId(visitId);
    setCurrentImpressionId(null);
    setCurrentImpressionVenueName(venueName);
    setImpressionReturnVenueId(null);
    setProfileFlash(null);
    setActiveSection('impression-form');
  }, []);

  const handleEditImpression = useCallback((impressionId: string) => {
    setCurrentImpressionId(impressionId);
    setCurrentVisitId(null);
    setImpressionReturnVenueId(null);
    setProfileFlash(null);
    setActiveSection('impression-edit');
  }, []);

  const handleVenueCreateImpression = useCallback((visitId: string, venueName: string) => {
    setCurrentVisitId(visitId);
    setCurrentImpressionId(null);
    setCurrentImpressionVenueName(venueName);
    setImpressionReturnVenueId(selectedVenueId);
    setProfileFlash(null);
    setVenueScreenOpen(false);
    setActiveSection('impression-form');
  }, [selectedVenueId]);

  const handleVenueEditImpression = useCallback((impressionId: string) => {
    setCurrentImpressionId(impressionId);
    setCurrentVisitId(null);
    setImpressionReturnVenueId(selectedVenueId);
    setProfileFlash(null);
    setVenueScreenOpen(false);
    setActiveSection('impression-edit');
  }, [selectedVenueId]);

  const handleReturnToProfile = useCallback((message: string | null = null) => {
    setCurrentVisitId(null);
    setCurrentImpressionId(null);
    setImpressionReturnVenueId(null);
    setProfileFlash(message);
    setActiveSection('profile');
  }, []);

  const handleReturnFromVenueImpression = useCallback((changed: boolean) => {
    const venueId = impressionReturnVenueId;
    setCurrentVisitId(null);
    setCurrentImpressionId(null);
    setImpressionReturnVenueId(null);

    if (!venueId) {
      setActiveSection('profile');
      return;
    }

    if (changed) setVenueDetailRevision((value) => value + 1);
    setSelectedVenueId(venueId);
    setActiveSection('map');
    setVenueScreenOpen(true);
    pushVenuePath(venueId);
  }, [impressionReturnVenueId]);

  const handleScanOpen = useCallback(() => {
    window.dispatchEvent(new Event('hopper:open-scanner'));
  }, []);

  const handleOpenSocialUser = useCallback((userId: string, returnSection: SocialReturnSection = 'feed') => {
    setCurrentSocialUserId(userId);
    setSocialReturnSection(returnSection);
    setVenueScreenOpen(false);
    setActiveSection('user-profile');
  }, []);

  const handleOpenFollowList = useCallback((
    userId: string,
    mode: FollowListMode,
    returnSection: FollowListReturnSection = 'user-profile',
  ) => {
    setFollowListUserId(userId);
    setFollowListMode(mode);
    setFollowListReturnSection(returnSection);
    setActiveSection('follow-list');
  }, []);

  const handleOpenNotifications = useCallback(() => {
    setVenueScreenOpen(false);
    setActiveSection('notifications');
  }, []);

  const handleOpenVenueFromSocial = useCallback((venueId: string) => {
    openVenueDetail(venueId, true);
  }, [openVenueDetail]);

  const handleShowNewPlaceOnMap = useCallback(async (venueId: string) => {
    try {
      const { venue } = await fetchVenueDetail(venueId);
      if (!venue || !venue.latitude || !venue.longitude) return;
      if (venue.city.id !== activeCity?.id) {
        const cityResult = await resolveCityContext({ city: venue.city.id });
        if (cityResult.status !== 'available') return;
        applyCityResolution(cityResult, 'manual');
        setMapData(null);
      }
      const selected: Venue = {
        id: venue.id,
        name: venue.name,
        address: venue.address,
        category: null,
        opening_status: venue.opening_status ?? 'unknown',
        verified_receipt_price: null,
        location: { latitude: venue.latitude, longitude: venue.longitude },
        geocode_required: false,
      };
      setPulseSelectedVenue(selected);
      setPulseFocusVenueId(venue.id);
      setSelectedVenueId(venue.id);
      setMobileDetailExpanded(false);
      setVenueScreenOpen(false);
      setActiveSection('map');
      pushShellSectionPath('map');
    } catch {
      // Public venue/city failures must not select phantom markers.
    }
  }, [activeCity?.id, applyCityResolution]);

  const showingVenueDetail = activeSection === 'map' && venueScreenOpen && Boolean(selectedVenueId);
  const cityName = showingVenueDetail && venueDetail ? venueDetail.city.name : activeCity?.name ?? venueDetail?.city.name ?? 'Выберите город';
  const cityId = showingVenueDetail && venueDetail ? venueDetail.city.id : activeCity?.id ?? '';
  const navSection = activeNavSection(activeSection);
  const authenticated = session?.authenticated === true;
  const currentUserId = authenticated ? session?.user?.id ?? null : null;
  const selectedVenueSaved = selectedVenueId ? savedVenueIds.has(selectedVenueId) : false;
  const selectedVenueSaving = selectedVenueId ? savingVenueId === selectedVenueId : false;
  const userContext = useUserContextData(session, cityId, cityName);
  const userContextActions = useUserContextActions({
    onSessionChange: setSession,
    onClose: () => setUserContextOpen(false),
  });

  const cityStatePanel = (
    <CityStatePanel
      status={cityStatus}
      policy={cityPolicy}
      detected={detectedLocation}
      resolving={cityResolving}
      onScan={handleScanOpen}
      onChoose={() => setCitySelectorOpen(true)}
      onLocate={locateCity}
      onPilotCity={() => { if (cityPolicy?.pilot_city) chooseCity(cityPolicy.pilot_city); }}
    />
  );

  let content: React.ReactNode;

  if (activeSection === 'impression-form' && currentVisitId) {
    content = (
      <ImpressionFormScreen
        visitId={currentVisitId}
        venueName={currentImpressionVenueName}
        onDone={(message) => {
          if (impressionReturnVenueId) handleReturnFromVenueImpression(true);
          else handleReturnToProfile(message);
        }}
        onCancel={() => {
          if (impressionReturnVenueId) handleReturnFromVenueImpression(false);
          else handleReturnToProfile();
        }}
      />
    );
  } else if (activeSection === 'impression-edit' && currentImpressionId) {
    content = (
      <ImpressionEditScreen
        impressionId={currentImpressionId}
        onDone={(message) => {
          if (impressionReturnVenueId) handleReturnFromVenueImpression(true);
          else handleReturnToProfile(message);
        }}
        onCancel={() => {
          if (impressionReturnVenueId) handleReturnFromVenueImpression(false);
          else handleReturnToProfile();
        }}
      />
    );
  } else if (activeSection === 'my-visits') {
    content = authenticated ? (
      <PulseVisitsScreen
        onBack={() => window.history.length > 1 ? window.history.back() : window.location.assign('/profile')}
        onOpenVenue={handleOpenVenueFromSocial}
      />
    ) : (
      <ProfileScreen cityId={cityId} cityName={cityName} onSessionChange={setSession} />
    );
  } else if (activeSection === 'my-saved') {
    content = authenticated ? (
      <PulseSavedPlacesScreen
        onBack={() => window.history.length > 1 ? window.history.back() : window.location.assign('/profile')}
        onOpenVenue={handleOpenVenueFromSocial}
      />
    ) : (
      <ProfileScreen cityId={cityId} cityName={cityName} onSessionChange={setSession} />
    );
  } else if (activeSection === 'my-friends') {
    content = authenticated && currentUserId ? (
      <FollowListScreen
        userId={currentUserId}
        mode="following"
        authenticated
        currentUserId={currentUserId}
        onModeChange={setFollowListMode}
        onBack={() => window.history.length > 1 ? window.history.back() : window.location.assign('/profile')}
        onOpenUser={(userId) => handleOpenSocialUser(userId, 'follow-list')}
      />
    ) : (
      <ProfileScreen cityId={cityId} cityName={cityName} onSessionChange={setSession} />
    );
  } else if (activeSection === 'profile') {
    content = (
      <ProfileScreen
        key={dataRevision}
        cityId={cityId}
        cityName={cityName}
        flash={profileFlash}
        onCreateImpression={handleCreateImpression}
        onEditImpression={handleEditImpression}
        onOpenFriends={() => {
          if (currentUserId) handleOpenFollowList(currentUserId, 'following', 'profile');
        }}
        onOpenUser={() => handleNavigate('profile')}
        onOpenVenue={handleOpenVenueFromSocial}
        onSessionChange={setSession}
      />
    );
  } else if (activeSection === 'feed') {
    content = (
      <FeedScreen
        cityId={cityId}
        cityName={cityName}
        authenticated={authenticated}
        userContext={userContext}
        userContextActions={userContextActions}
        onScan={handleScanOpen}
        onOpenUser={(userId) => handleOpenSocialUser(userId, 'feed')}
        onOpenVenue={handleOpenVenueFromSocial}
        onShowVenueOnMap={(venueId) => { void handleShowNewPlaceOnMap(venueId); }}
        cityStatePanel={cityStatePanel}
      />
    );
  } else if (activeSection === 'user-profile' && currentSocialUserId) {
    content = (
      <UserProfileScreen
        userId={currentSocialUserId}
        authenticated={authenticated}
        currentUserId={currentUserId}
        onBack={() => setActiveSection(socialReturnSection)}
        onOpenFollowers={(userId) => handleOpenFollowList(userId, 'followers', 'user-profile')}
        onOpenFollowing={(userId) => handleOpenFollowList(userId, 'following', 'user-profile')}
      />
    );
  } else if (activeSection === 'follow-list' && followListUserId) {
    content = (
      <FollowListScreen
        userId={followListUserId}
        mode={followListMode}
        authenticated={authenticated}
        currentUserId={currentUserId}
        onModeChange={setFollowListMode}
        onBack={() => setActiveSection(followListReturnSection)}
        onOpenUser={(userId) => handleOpenSocialUser(userId, 'follow-list')}
      />
    );
  } else if (activeSection === 'notifications') {
    content = (
      <NotificationsScreen
        authenticated={authenticated}
        onBack={() => setActiveSection('feed')}
        onOpenUser={(userId) => handleOpenSocialUser(userId, 'notifications')}
        onOpenVenue={handleOpenVenueFromSocial}
      />
    );
  } else if (showingVenueDetail && selectedVenueId) {
    content = (
      <VenueDetailScreen
        venueId={selectedVenueId}
        detail={venueDetail}
        loading={venueDetailLoading}
        authenticated={authenticated}
        saved={selectedVenueSaved}
        saving={selectedVenueSaving}
        onToggleSaved={() => void handleToggleSaved(selectedVenueId)}
        onClose={closeVenueDetail}
        onOpenVenue={(venueId) => openVenueDetail(venueId, true)}
        onCreateImpression={handleVenueCreateImpression}
        onEditImpression={handleVenueEditImpression}
        onScan={handleScanOpen}
      />
    );
  } else if (activeSection === 'offers' && !activeCity) {
    content = cityStatePanel;
  } else if (activeSection === 'offers') {
    content = (
      <OffersScreen
        city={offerContext.city}
        cityName={activeCity?.name}
        mode={offerContext.mode}
        venueId={offerContext.venueId}
        onOpenActivation={handleActivationOpen}
        userContext={userContext}
        userContextActions={userContextActions}
      />
    );
  } else if (activeSection === 'activation-detail' && currentActivationId) {
    content = (
      <ActivationDetailScreen
        activationId={currentActivationId}
        context={offerContext}
        offerId={currentOfferId}
        onBack={handleBackToOffers}
        onExpired={() => setActiveSection('expired')}
        onRedeemed={() => setActiveSection('redeemed')}
      />
    );
  } else if (activeSection === 'redeemed' && currentActivationId) {
    content = <RedeemedScreen activationId={currentActivationId} onBackToOffers={handleBackToOffers} />;
  } else if (activeSection === 'expired') {
    content = <ExpiredScreen onBackToOffers={handleBackToOffers} />;
  } else if (!activeCity) {
    content = <main className="map-page city-context-page">{cityStatePanel}</main>;
  } else {
    content = (
      <>
        <main className="map-page map-reference-map-page" style={showingVenueDetail ? { display: 'none' } : undefined}>
          <FilterRail
            filters={mapFilters}
            onChange={setMapFilters}
            onReset={() => {
              setMapFilters({ categories: [], avgCheck: [], openNow: false, hasOffers: false, hasEvents: false });
              setNearbyLocation(null);
              setNearbyPermissionError(null);
            }}
            nearbyActive={nearbyLocation !== null}
            nearbyRequesting={nearbyRequesting}
            nearbyError={nearbyPermissionError}
            onToggleNearby={handleToggleNearby}
            refreshing={mapRefreshing}
            error={mapFilterError}
            resultCount={mapData?.venues.length ?? null}
          />

          <section className="map-workspace" aria-label="Карта Хоппер">
            <div className="mobile-map-controls">
              <div className="mobile-search-row">
                <label className="mobile-search">
                  <Icon name="search" />
                  <input aria-label="Поиск заведений" placeholder="Поиск заведений" type="search" />
                </label>
                <button className="filter-button" type="button" onClick={() => setMapFiltersOpen(true)}>
                  <HopperIcon name="filter" size={18} />
                  <span>Фильтры</span>
                </button>
              </div>
            </div>

            {mapData ? (
              <YandexMap
                apiKey={mapsKey}
                city={mapData.city}
                onVenueSelect={handleVenueSelect}
                selectedVenueId={selectedVenue?.id ?? null}
                focusVenueId={pulseFocusVenueId === selectedVenueId ? pulseFocusVenueId : null}
                venues={venues}
                visible={!showingVenueDetail}
                discoveryStatus={mapFilterError ?? (mapRefreshing ? 'Обновляем результаты…' : mapData.venues.length === 0 ? 'По выбранным фильтрам ничего не найдено' : null)}
              />
            ) : (
              <div className="map-stage map-stage--loading">
                <div className="map-state-card" role="status">
                  <span className="map-state-card__pulse" />
                  <strong>{error ?? 'Загружаем места'}</strong>
                  <span>{loading ? `Получаем места для города ${activeCity.name}…` : 'Не удалось получить места. Можно повторить запрос.'}</span>
                  {!loading && (
                    <button className="map-state-card__retry" type="button" onClick={() => setMapLoadRevision((value) => value + 1)}>
                      Повторить
                    </button>
                  )}
                </div>
              </div>
            )}

            {!mapFiltersOpen && <MobileVenueSheet
              detail={venueDetail}
              expanded={mobileDetailExpanded}
              authenticated={authenticated}
              saved={selectedVenueSaved}
              saving={selectedVenueSaving}
              onToggleSaved={() => {
                if (selectedVenue) void handleToggleSaved(selectedVenue.id);
              }}
              onOpen={() => {
                if (selectedVenue) openVenueDetail(selectedVenue.id, true);
              }}
              onOpenOffers={handleOpenSelectedVenueOffers}
              onClose={clearMapVenueSelection}
              onToggle={() => setMobileDetailExpanded((value) => !value)}
              venue={selectedVenue}
            />}

            <BottomSheet
              open={mapFiltersOpen}
              onClose={() => setMapFiltersOpen(false)}
              title="Фильтры карты"
              rootClassName="map-filters-sheet"
              maxHeight="72dvh"
            >
              <div className="map-filters-sheet__content">
                <div className="filter-group">
                  <strong>Категории</strong>
                  <CategoryChips filters={mapFilters} onChange={setMapFilters} />
                </div>
                <div className="filter-group">
                  <strong>Средний чек</strong>
                  <AverageCheckFilter
                    tiers={mapFilters.avgCheck}
                    onChange={(avgCheck) => setMapFilters((current) => ({ ...current, avgCheck }))}
                  />
                </div>
                <div className="filter-group">
                  <strong>Сейчас важно</strong>
                  <div className="map-filter-stack">
                    <DiscoveryToggles filters={mapFilters} onChange={setMapFilters} />
                    <NearbyToggle
                      active={nearbyLocation !== null}
                      requesting={nearbyRequesting}
                      error={nearbyPermissionError}
                      onToggle={handleToggleNearby}
                    />
                  </div>
                </div>
                <button
                  className="map-filters-reset"
                  type="button"
                  disabled={mapFilters.categories.length === 0 && mapFilters.avgCheck.length === 0 && !mapFilters.openNow && !mapFilters.hasOffers && !mapFilters.hasEvents && nearbyLocation === null}
                  onClick={() => {
                    setMapFilters({ categories: [], avgCheck: [], openNow: false, hasOffers: false, hasEvents: false });
                    setNearbyLocation(null);
                    setNearbyPermissionError(null);
                  }}
                >
                  Сбросить все
                </button>
              </div>
            </BottomSheet>
          </section>

          <div className="map-right-rail">
            <VenuePanel
              detail={venueDetail}
              loading={venueDetailLoading}
              authenticated={authenticated}
              saved={selectedVenueSaved}
              saving={selectedVenueSaving}
              onToggleSaved={() => {
                if (selectedVenue) void handleToggleSaved(selectedVenue.id);
              }}
              onOpen={() => {
                if (selectedVenue) openVenueDetail(selectedVenue.id, true);
              }}
              onOpenOffers={handleOpenSelectedVenueOffers}
              venue={selectedVenue}
            />
          </div>
        </main>

      </>
    );
  }

  return (
    <div className="hopper-app-shell">
      <DesktopHeader
        cityName={cityName}
        activeSection={navSection}
        session={session}
        consistentAvatar={navSection === 'feed' || navSection === 'map' || navSection === 'offers' || navSection === 'profile'}
        onNavigate={handleNavigate}
        onNotifications={handleOpenNotifications}
        onCityClick={() => setCitySelectorOpen(true)}
      />
      <MobileHeader
        cityName={cityName}
        userContext={userContext}
        consistentAvatar={navSection === 'feed' || navSection === 'map' || navSection === 'offers' || navSection === 'profile'}
        onNotifications={handleOpenNotifications}
        onOpenUserContext={() => setUserContextOpen(true)}
        onCityClick={() => setCitySelectorOpen(true)}
      />

      {content}

      <BottomSheet
        open={citySelectorOpen}
        onClose={() => setCitySelectorOpen(false)}
        title="Выберите город"
        rootClassName="city-selector-sheet"
        maxHeight="72dvh"
      >
        <div className="city-selector-sheet__content">
          {cityPolicy?.mode === 'pilot_only' ? (
            <div className="city-selector-results">
              <p><strong>Хоппер пока работает во Владивостоке.</strong></p>
              <p>Другие города пока недоступны для публичного просмотра. Чек из другого города можно отсканировать — после проверки данные сохранятся.</p>
              {cityPolicy.pilot_city && (
                <button
                  type="button"
                  disabled={choosingCityId !== null}
                  onClick={() => chooseCity(cityPolicy.pilot_city!)}
                >
                  <strong>{choosingCityId === cityPolicy.pilot_city.id ? 'Открываем…' : cityPolicy.pilot_city.name}</strong>
                  {cityPolicy.pilot_city.region_name && <span>{cityPolicy.pilot_city.region_name}</span>}
                </button>
              )}
              <button className="city-locate-button" type="button" onClick={handleScanOpen}>Сканировать чек</button>
              {citySelectionError && <div className="city-selector-feedback is-error" role="alert">{citySelectionError}</div>}
            </div>
          ) : (
            <>
              <label className="city-selector-search">
                <Icon name="search" />
                <input
                  autoFocus
                  aria-label="Поиск города"
                  value={citySearch}
                  onChange={(event) => setCitySearch(event.target.value)}
                  placeholder="Город или регион"
                  type="search"
                />
              </label>
              <button className="city-locate-button" type="button" disabled={cityResolving} onClick={locateCity}>
                <Icon name="location" />
                {cityResolving ? 'Определяем город…' : 'Определить мой город'}
              </button>

              {citySelectionError && <div className="city-selector-feedback is-error" role="alert">{citySelectionError}</div>}
              {citySearchState === 'loading' && <div className="city-selector-feedback" role="status">Ищем доступные города…</div>}
              {citySearchState === 'error' && (
                <div className="city-selector-feedback is-error" role="alert">
                  <span>Не удалось загрузить города.</span>
                  <button type="button" onClick={() => setCitySearchRevision((value) => value + 1)}>Повторить</button>
                </div>
              )}
              {citySearchState === 'ready' && (
                <div className="city-selector-results">
                  {cityChoices.map((city) => (
                    <button
                      key={city.id}
                      type="button"
                      disabled={choosingCityId !== null}
                      onClick={() => chooseCity(city)}
                    >
                      <strong>{choosingCityId === city.id ? 'Проверяем…' : city.name}</strong>
                      {city.region_name && <span>{city.region_name}</span>}
                    </button>
                  ))}
                  {cityChoices.length === 0 && <p>По запросу доступных городов не найдено.</p>}
                </div>
              )}
            </>
          )}
        </div>
      </BottomSheet>

      <UserContextSheet
        open={userContextOpen}
        onClose={() => setUserContextOpen(false)}
        data={userContext}
        actions={userContextActions}
      />

      {!showingVenueDetail && (
        <MobileBottomNav activeSection={navSection} onNavigate={handleNavigate} onScan={handleScanOpen} />
      )}
    </div>
  );
}
